"use client";

import * as React from "react";
import * as chrono from "chrono-node";
import { format } from "date-fns";
import { CalendarDays } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";

function formatDateTime(value) {
  return format(value, "MMMM dd, yyyy 'at' hh:mm aa");
}

export function DateTimePicker({ value, onChange }) {
  const [date, setDate] = React.useState(value);
  const [inputValue, setInputValue] = React.useState("");
  const [isPickerOpen, setIsPickerOpen] = React.useState(false);

  const timeScrollAreaRef = React.useRef(null);

  const setDateTime = React.useCallback(
    (nextDate) => {
      setDate(nextDate);
      onChange?.(nextDate);
      setInputValue(nextDate ? formatDateTime(nextDate) : "");
    },
    [onChange]
  );

  React.useEffect(() => {
    setDate(value);
    setInputValue(value ? formatDateTime(value) : "");
  }, [value]);

  const timeOptions = React.useMemo(() => {
    return Array.from({ length: 96 }, (_, index) => {
      const hour24 = Math.floor(index / 4);
      const minute = (index % 4) * 15;
      const optionDate = new Date();

      optionDate.setHours(hour24, minute, 0, 0);

      return {
        label: format(optionDate, "hh:mm aa"),
        hour24,
        minute,
      };
    });
  }, []);

  const selectedTimeOption = React.useMemo(() => {
    if (!date) return null;

    const roundedMinutes = Math.round(date.getMinutes() / 15) * 15;
    const hourCarry = roundedMinutes === 60 ? 1 : 0;

    return {
      hour24: (date.getHours() + hourCarry) % 24,
      minute: roundedMinutes % 60,
    };
  }, [date]);

  const scrollToSelectedTime = React.useCallback(() => {
    const scrollAreaRoot = timeScrollAreaRef.current;
    if (!scrollAreaRoot) return;

    const viewport = scrollAreaRoot.querySelector(
      "[data-radix-scroll-area-viewport]"
    );
    const selectedElement = scrollAreaRoot.querySelector(
      "[data-time-option='selected']"
    );

    if (!selectedElement) return;

    if (!viewport) {
      selectedElement.scrollIntoView({ block: "center" });
      return;
    }

    const targetTop =
      selectedElement.offsetTop -
      viewport.clientHeight / 2 +
      selectedElement.offsetHeight / 2;

    viewport.scrollTo({ top: Math.max(0, targetTop) });
  }, []);

  React.useEffect(() => {
    if (!isPickerOpen) return;

    const timeoutId = setTimeout(scrollToSelectedTime, 0);

    return () => clearTimeout(timeoutId);
  }, [isPickerOpen, selectedTimeOption?.hour24, selectedTimeOption?.minute, scrollToSelectedTime]);

  const handleDateSelect = (selectedDate) => {
    if (selectedDate) {
      const newDate = new Date(selectedDate);
      if (date) {
        newDate.setHours(date.getHours());
        newDate.setMinutes(date.getMinutes());
      }
      setDateTime(newDate);
    }
  };

  const handleTimeSelect = ({ hour24, minute }) => {
    const baseDate = date ? new Date(date) : new Date();
    baseDate.setHours(hour24, minute, 0, 0);
    setDateTime(baseDate);
  };

  const applyNaturalInput = React.useCallback(() => {
    const raw = inputValue.trim();

    if (!raw) {
      setDate(undefined);
      onChange?.(undefined);
      return;
    }

    const parsed = chrono.parseDate(raw, date || new Date());
    if (!parsed) return;

    setDateTime(parsed);
  }, [inputValue, date, onChange, setDateTime]);

  const handleInputChange = (event) => {
    setInputValue(event.target.value);
  };

  const handleInputKeyDown = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      applyNaturalInput();
    }
  };

  return (
    <div className="relative">
      <div className="relative">
        <Input
          type="text"
          placeholder={'Write any time here. For instance, "tomorrow at 9am" or "in 2 hours"'}
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={handleInputKeyDown}
          onBlur={applyNaturalInput}
          className="pl-10 h-8 rounded-lg"
        />
        <Popover open={isPickerOpen} onOpenChange={setIsPickerOpen}>
          <PopoverTrigger asChild>
            <Button
              size="icon"
              variant="ghost"
              className="absolute left-1 top-1/2 size-8 -translate-y-1/2 rounded-sm border border-transparent text-muted-foreground hover:bg-transparent hover:text-foreground hover:border-primary/35 hover:shadow-[0_0_0_2px_hsl(var(--primary)/0.12)]"
            >
              <span className="sr-only">Open date and time picker</span>
              <CalendarDays className="size-4" />
            </Button>
          </PopoverTrigger>
          <PopoverContent
            className="w-auto p-0"
            onOpenAutoFocus={(event) => {
              event.preventDefault();
              requestAnimationFrame(scrollToSelectedTime);
            }}
          >
            <div className="flex flex-col sm:flex-row sm:items-stretch">
              <Calendar
                mode="single"
                selected={date}
                onSelect={handleDateSelect}
                className="sm:h-[350px]"
                initialFocus
              />
              <div className="border-t border-border p-2 sm:flex sm:h-[350px] sm:w-32 sm:border-l sm:border-t-0">
                <div className="relative sm:flex sm:min-h-0 sm:flex-1">
                  <ScrollArea
                    ref={timeScrollAreaRef}
                    className="h-48 sm:h-full sm:flex-1"
                    style={{
                      WebkitMaskImage: "linear-gradient(to bottom, transparent, black 18px, black calc(100% - 18px), transparent)",
                      maskImage: "linear-gradient(to bottom, transparent, black 18px, black calc(100% - 18px), transparent)",
                    }}
                  >
                    <div className="flex flex-col items-center gap-1 py-2 pr-2">
                      {timeOptions.map((option) => (
                        <Button
                          key={`${option.hour24}:${option.minute}`}
                          data-time-option={
                            selectedTimeOption &&
                            option.hour24 === selectedTimeOption.hour24 &&
                            option.minute === selectedTimeOption.minute
                              ? "selected"
                              : undefined
                          }
                          variant={
                            selectedTimeOption &&
                            selectedTimeOption.hour24 === option.hour24 &&
                            selectedTimeOption.minute === option.minute
                              ? "default"
                              : "ghost"
                          }
                          className="w-24 justify-center rounded-md px-2"
                          onClick={() => handleTimeSelect(option)}
                        >
                          {option.label}
                        </Button>
                      ))}
                    </div>
                  </ScrollArea>
                </div>
              </div>
            </div>
          </PopoverContent>
        </Popover>
      </div>
    </div>
  );
}
